import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type CrossClientQueryClientQueryMode, CrossClientQueryClientQueryModes, type CrossClientQueryDataSource } from '../../../models/CrossClient'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useGetMeQuery } from '../../../store/user/user'
import { CROSS_CLIENT_QUERY_SUPPORTED_INTEGRATIONS, IntegrationCategory, IntegrationServiceId } from '../../../common/integrations'
import { isCrossClientQueryClientQueryMode } from '../utils'

const COOKIE_OPTIONS = { path: '/', maxAge: 60 * 60 * 24 * 365 }
const CCQ_CLIENT_QUERY_MODE_COOKIE = 'ccqClientQueryMode'
const CCQ_SOURCES_COOKIE = 'ccqSources'

export const MAX_INTEGRATION_SOURCES = 2

const capIntegrationSources = (sources: CrossClientQueryDataSource[]): CrossClientQueryDataSource[] =>
  sources.reduce<CrossClientQueryDataSource[]>(
    (kept, source) =>
      source.type === 'integration' && kept.filter((keptSource) => keptSource.type === 'integration').length >= MAX_INTEGRATION_SOURCES
        ? kept
        : [...kept, source],
    [],
  )

const isValidSourcesArray = (value: unknown): value is CrossClientQueryDataSource[] =>
  Array.isArray(value) && value.every((s) => s && typeof s === 'object' && typeof (s as { type?: unknown }).type === 'string')

interface SupportedIntegrations {
  supportedUserIntegrationsByCategory: Partial<Record<IntegrationCategory, IntegrationServiceId[]>>
  supportedServiceIds: IntegrationServiceId[]
  integrationDefaults: CrossClientQueryDataSource[]
  isLoading: boolean
}

export const useSupportedCrossClientIntegrations = (): SupportedIntegrations => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser, isLoading } = useGetMeQuery({ accountId })

  const supportedUserIntegrationsByCategory = useMemo(() => {
    if (!currentUser?.integrations) {
      return {} as Partial<Record<IntegrationCategory, IntegrationServiceId[]>>
    }
    const userServiceIds = new Set(currentUser.integrations.map((i) => i.serviceId))
    return Object.entries(CROSS_CLIENT_QUERY_SUPPORTED_INTEGRATIONS).reduce(
      (acc, [category, supportedServiceIds]) => {
        const matchingServiceIds = supportedServiceIds.filter((serviceId) => userServiceIds.has(serviceId))
        if (matchingServiceIds.length > 0) {
          acc[category as IntegrationCategory] = matchingServiceIds
        }
        return acc
      },
      {} as Partial<Record<IntegrationCategory, IntegrationServiceId[]>>,
    )
  }, [currentUser])

  const supportedServiceIds = useMemo(() => Object.values(supportedUserIntegrationsByCategory).flat(), [supportedUserIntegrationsByCategory])

  const integrationDefaults = useMemo<CrossClientQueryDataSource[]>(() => {
    const defaults: CrossClientQueryDataSource[] = []
    const crmIntegrations = supportedUserIntegrationsByCategory[IntegrationCategory.CRM]
    if (crmIntegrations?.length) {
      defaults.push({ type: 'integration', integrationType: crmIntegrations[0] })
    }
    const planningIntegrations = supportedUserIntegrationsByCategory[IntegrationCategory.PLANNING]
    if (planningIntegrations?.length) {
      defaults.push({ type: 'integration', integrationType: planningIntegrations[0] })
    }
    return defaults
  }, [supportedUserIntegrationsByCategory])

  return { supportedUserIntegrationsByCategory, supportedServiceIds, integrationDefaults, isLoading }
}

interface CrossClientQuerySettingsOptions {
  initialSources?: CrossClientQueryDataSource[]
  initialClientQueryMode?: CrossClientQueryClientQueryMode
}

interface CrossClientQuerySettings {
  clientQueryMode: CrossClientQueryClientQueryMode
  setClientQueryMode: (mode: CrossClientQueryClientQueryMode) => void
  sources: CrossClientQueryDataSource[]
  sourcesReady: boolean
  setSources: (sources: CrossClientQueryDataSource[]) => void
}

export const useCrossClientQuerySettings = ({ initialSources, initialClientQueryMode }: CrossClientQuerySettingsOptions = {}): CrossClientQuerySettings => {
  const { hasScope } = useZocksAuth()
  const isAdmin = hasScope('**', 'cross_client_query:admin', [])
  const { integrationDefaults, supportedServiceIds, isLoading } = useSupportedCrossClientIntegrations()

  const [cookies, setCookie] = useCookies([CCQ_CLIENT_QUERY_MODE_COOKIE, CCQ_SOURCES_COOKIE])

  const cookieSources = useMemo<CrossClientQueryDataSource[] | null>(() => {
    const raw = cookies[CCQ_SOURCES_COOKIE]
    return isValidSourcesArray(raw) ? raw : null
  }, [cookies])

  const [clientQueryMode, setClientQueryModeState] = useState<CrossClientQueryClientQueryMode>(() => {
    if (initialClientQueryMode) {
      return initialClientQueryMode
    }
    const cookieValue = cookies[CCQ_CLIENT_QUERY_MODE_COOKIE]
    if (isCrossClientQueryClientQueryMode(cookieValue)) {
      return cookieValue
    }
    return isAdmin ? CrossClientQueryClientQueryModes.WITH_SHARED : CrossClientQueryClientQueryModes.ONLY_OWNED
  })

  const setClientQueryMode = useCallback(
    (mode: CrossClientQueryClientQueryMode) => {
      setClientQueryModeState(mode)
      setCookie(CCQ_CLIENT_QUERY_MODE_COOKIE, mode, COOKIE_OPTIONS)
    },
    [setCookie],
  )

  const [storedSources, setStoredSources] = useState<CrossClientQueryDataSource[] | null>(cookieSources)
  const [visitSources, setVisitSources] = useState<CrossClientQueryDataSource[] | null>(initialSources ?? null)

  const isSupportedSource = useCallback(
    (source: CrossClientQueryDataSource) => source.type !== 'integration' || supportedServiceIds.includes(source.integrationType as IntegrationServiceId),
    [supportedServiceIds],
  )

  const sources = useMemo(() => {
    const base = visitSources ?? storedSources ?? integrationDefaults
    return capIntegrationSources(isLoading ? base : base.filter(isSupportedSource))
  }, [visitSources, storedSources, integrationDefaults, isLoading, isSupportedSource])

  useEffect(() => {
    if (isLoading || storedSources == null) {
      return
    }
    const reconciled = storedSources.filter(isSupportedSource)
    if (reconciled.length !== storedSources.length) {
      setCookie(CCQ_SOURCES_COOKIE, JSON.stringify(reconciled), COOKIE_OPTIONS)
    }
  }, [isLoading, storedSources, isSupportedSource, setCookie])

  const setSources = useCallback(
    (next: CrossClientQueryDataSource[]) => {
      setVisitSources(null)
      setStoredSources(next)
      setCookie(CCQ_SOURCES_COOKIE, JSON.stringify(next), COOKIE_OPTIONS)
    },
    [setCookie],
  )

  return { clientQueryMode, setClientQueryMode, sources, sourcesReady: !isLoading, setSources }
}
